import { Activity, ArrowRight, Blocks, Bot, BrainCircuit, Camera, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Code2, Database, Download, ExternalLink, Gauge, GitBranch, Github, Gitlab, Home, Linkedin, LockKeyhole, MapPin, Network, Pause, Play, Send, Shield, ShieldCheck, SlidersHorizontal, Sparkles, UserRound } from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PRIVACY_VERSION, TERMS_VERSION } from "../../shared/types";
import { APP_VERSION } from "../../shared/version";
import { activeHomepageUpdate, homepageUpdate } from "../../shared/homepage-update";
import { colleagueQuotes } from "../../shared/colleague-quotes";
import { buildResumeDraft, type ResumeDraft } from "../../shared/resume";
import { executiveSummaryVariants } from "../../shared/executive-summary-variants";
import { experienceDossier } from "../../shared/experience";
import { keyInsightCards, type InsightCard } from "../../shared/insights";
import { mediaSnippets, mediaSnapshot, mediaStats } from "../../shared/footprint";
import { odinGallery, odinImagePath, odinImageWidths } from "../../shared/odin-gallery";
import { contactPrefill, contactSubjects } from "../../shared/contact-prefill";
import { defaultPhoneCountryCode, getPhoneCountry } from "../../shared/phone-codes";
import { generateAiComponent, submitContact } from "../api";
import { SUMMARY_GENERATION_LIMIT, summaryGenerationLimit } from "../summary-generation-limit";
import { PhoneNumberField } from "./PhoneNumberField";
import { HomepageUpdate } from "./HomepageUpdate";
import { SimulatedAiPre, SimulatedExecutiveSummary, SimulatedInsightContent } from "./SimulatedAiReveal";
import { ThinkingStatus } from "./ThinkingStatus";
import { TurnstileWidget } from "./TurnstileWidget";
import "./home-feedback.css";

function shuffleItems<T>(items: T[]) {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const targetIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[targetIndex]] = [shuffled[targetIndex], shuffled[index]];
  }
  return shuffled;
}

const INSIGHT_CARD_COUNT = 3;
const INSIGHT_CLICK_LIMIT = 5;
const MIN_GENERATION_LOADING_MS = 700;
const MIN_SUMMARY_LOADING_MS = 2_050;
const QUOTE_ROTATION_MS = 7000;

function getInsightWindow(cards: InsightCard[], offset: number) {
  return Array.from({ length: Math.min(INSIGHT_CARD_COUNT, cards.length) }, (_, index) => cards[(offset + index) % cards.length]);
}

function insightTitleKey(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function keepFreshInsights(items: InsightCard[], recentTitles: string[]) {
  const recent = new Set(recentTitles.map(insightTitleKey));
  const seen = new Set<string>();
  const fresh = items.filter((item) => {
    const key = insightTitleKey(item.title);
    if (recent.has(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (fresh.length !== INSIGHT_CARD_COUNT) throw new Error("component_ai_repeated_insights");
  return fresh;
}

const insightAngles = [
  "product leadership across agentic AI and cybersecurity",
  "globally distributed defensive systems and customer trust",
  "public influence through writing, speaking, and media reach",
  "commercially useful products built from deep technical systems",
  "executive-ready communication around complex security work",
];

function buildInsightPrompt(recentTitles: string[]) {
  const angle = insightAngles[Math.floor(Math.random() * insightAngles.length)];
  return [
    "Generate a fresh, diverse set of recruiter-relevant Key Insights for Omer Yoachimik.",
    "Base the cards on the approved key insights, experience dossier, and public footprint context.",
    "Each card should have a distinct title and one short sentence or data point of no more than 14 words and 110 characters.",
    `Use this refresh angle: ${angle}.`,
    `Refresh nonce: ${crypto.randomUUID()}.`,
    `Do not repeat or closely paraphrase these recent card titles: ${recentTitles.join(", ") || "none"}.`,
  ].join("\n");
}

function waitForMinimumLoading(startedAt: number, minimumMs = MIN_GENERATION_LOADING_MS) {
  const remaining = minimumMs - (Date.now() - startedAt);
  return remaining > 0 ? new Promise((resolve) => window.setTimeout(resolve, remaining)) : Promise.resolve();
}

type ExecutiveSummaryContent = {
  lead: string;
  highlights: string[];
};

function executiveSummaryText(summary: ExecutiveSummaryContent) {
  return [summary.lead, ...summary.highlights].join(" ");
}

function cachedExecutiveSummary(index: number): ExecutiveSummaryContent {
  const summary = executiveSummaryVariants[index];
  return { lead: summary.lead, highlights: [...summary.highlights] };
}

const summaryAngles = [
  "product leadership across agentic AI and cybersecurity",
  "turning complex defensive systems into customer trust",
  "executive communication and public industry influence",
  "operational product judgment at Cloudflare scale",
  "bridging technical depth, customer needs, and measurable products",
];

function buildExecutiveSummaryPrompt(recentSummaries: string[]) {
  const angle = summaryAngles[Math.floor(Math.random() * summaryAngles.length)];
  return [
    "Generate a new executive summary for Omer Yoachimik's homepage.",
    "Base it on the approved key insights, experience dossier, and public footprint context.",
    "Use one short lead and two concise, evidence-led bullet points for quick scanning.",
    "Keep the lead to 20-42 words, each bullet to 5-16 words, and the entire summary to 70 words or fewer.",
    "Synthesize the evidence into a polished, recruiter-facing summary instead of listing facts.",
    `Use this summary angle: ${angle}.`,
    `Refresh nonce: ${crypto.randomUUID()}.`,
    `Avoid repeating or closely paraphrasing these recent summaries: ${recentSummaries.join(" || ") || "none"}.`,
  ].join("\n");
}

function SummaryThinkingText() {
  return (
    <p className="simulated-ai-text summary-thinking" aria-busy="true" role="status">
      <ThinkingStatus />
    </p>
  );
}

function CuratedExecutiveSummary({ summary }: { summary: ExecutiveSummaryContent }) {
  return (
    <div className="summary-content">
      <p>{summary.lead}</p>
      {summary.highlights.length > 0 && <ul>{summary.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}</ul>}
    </div>
  );
}

function ExecutiveSummaryCard({ componentAiEnabled }: { componentAiEnabled: boolean }) {
  const [summaryIndex, setSummaryIndex] = useState(0);
  const [summaryContent, setSummaryContent] = useState<ExecutiveSummaryContent>(() => cachedExecutiveSummary(0));
  const [summaryRevision, setSummaryRevision] = useState(0);
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [summaryClickCount, setSummaryClickCount] = useState(() => summaryGenerationLimit.count());
  const recentSummaries = useRef<string[]>([]);
  const summaryLimitReached = summaryClickCount >= SUMMARY_GENERATION_LIMIT;
  const summaryRequestPending = useRef(false);
  const refreshSummary = useCallback(async () => {
    if (summaryRequestPending.current) return;
    summaryRequestPending.current = true;
    const startedAt = Date.now();
    setSummaryBusy(true);
    try {
      let nextSummary: ExecutiveSummaryContent;
      if (componentAiEnabled) {
        try {
          nextSummary = await generateAiComponent<ExecutiveSummaryContent>("executive-summary", buildExecutiveSummaryPrompt(recentSummaries.current));
        } catch {
          const nextIndex = (summaryIndex + 1) % executiveSummaryVariants.length;
          setSummaryIndex(nextIndex);
          nextSummary = cachedExecutiveSummary(nextIndex);
        }
      } else {
        const nextIndex = (summaryIndex + 1) % executiveSummaryVariants.length;
        setSummaryIndex(nextIndex);
        nextSummary = cachedExecutiveSummary(nextIndex);
      }
      recentSummaries.current = [...recentSummaries.current, executiveSummaryText(nextSummary)].slice(-SUMMARY_GENERATION_LIMIT);
      await waitForMinimumLoading(startedAt, MIN_SUMMARY_LOADING_MS);
      setSummaryContent(nextSummary);
      setSummaryRevision((value) => value + 1);
    } finally {
      summaryRequestPending.current = false;
      setSummaryBusy(false);
    }
  }, [componentAiEnabled, summaryIndex]);

  const generateNewSummary = useCallback(() => {
    if (summaryRequestPending.current || !summaryGenerationLimit.reserve()) return;
    setSummaryClickCount(summaryGenerationLimit.count());
    void refreshSummary();
  }, [refreshSummary]);

  return (
    <section className="executive-card home-summary-card" aria-labelledby="summary-title">
      <div className="summary-topline">
        <span><Sparkles size={15} /> {componentAiEnabled ? "AI executive summary" : "Curated executive summary"}</span>
      </div>
      <h2 id="summary-title">Executive summary</h2>
      {summaryBusy
        ? <SummaryThinkingText />
        : componentAiEnabled
        ? <SimulatedExecutiveSummary key={`${summaryIndex}-${summaryRevision}`} {...summaryContent} />
        : <CuratedExecutiveSummary key={`${summaryIndex}-${summaryRevision}`} summary={summaryContent} />}
      <button type="button" className={`regenerate-button${summaryLimitReached ? "" : " summary-generate-highlight"}`} onClick={generateNewSummary} disabled={summaryBusy || summaryLimitReached} aria-busy={summaryBusy} aria-describedby={summaryLimitReached ? "summary-limit-note" : undefined}>
        {summaryBusy
          ? <ThinkingStatus compact />
          : <><Sparkles size={16} /> {summaryLimitReached ? "Two summaries generated" : "Generate a new summary"}</>}
      </button>
      {summaryLimitReached && <p className="summary-limit-note" id="summary-limit-note" role="status">You&apos;ve used your two generations this session. Explore the <Link to="/insights">Insights</Link> for more.</p>}
    </section>
  );
}

export function HomePage({ componentAiEnabled }: { componentAiEnabled: boolean }) {
  const update = activeHomepageUpdate(homepageUpdate);
  return (
    <section className={`home-page${update ? " home-page--with-update" : ""}`}>
      <header className="home-welcome-header">
        <div className="home-welcome-copy">
          <p className="home-welcome">Welcome to Omer&apos;s AI-powered, experimental, interactive bio.</p>
          <Link className="home-feedback-link" to="/contact?subject=website-feedback">Let me know what you think <ArrowRight size={14} aria-hidden="true" /></Link>
        </div>
        <span className="version-chip home-version">Web app v{APP_VERSION} (Beta)</span>
      </header>
      {update && <HomepageUpdate update={update} />}
      <div className="home-hero-grid">
        <div className="hero-copy home-hero-copy">
          <h1>Omer Yoachimik</h1>
          <nav className="home-profile-links" aria-label="Omer Yoachimik's profiles and repositories">
            <a className="profile-link" href="https://www.linkedin.com/in/omeryoachimik" target="_blank" rel="noreferrer">
              <Linkedin size={17} aria-hidden="true" />
              <span>LinkedIn</span>
            </a>
            <a className="profile-link" href="https://github.com/omeryo" target="_blank" rel="noreferrer">
              <Github size={17} aria-hidden="true" />
              <span>GitHub</span>
            </a>
            <a className="profile-link" href="https://gitlab.com/omery1k" target="_blank" rel="noreferrer">
              <Gitlab size={17} aria-hidden="true" />
              <span>GitLab</span>
            </a>
          </nav>
        </div>
        <ExecutiveSummaryCard componentAiEnabled={componentAiEnabled} />
      </div>
    </section>
  );
}

function ExperienceCaseStudyCard({ caseStudy, index, expanded, onToggle }: {
  caseStudy: (typeof experienceDossier.caseStudies)[number];
  index: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const panelId = `experience-panel-${caseStudy.id}`;
  const primaryOutcome = caseStudy.outcomes[0];
  return (
    <article className={`experience-item ${expanded ? "expanded" : ""}`}>
      <button
        type="button"
        className="experience-toggle"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="experience-index">{String(index + 1).padStart(2, "0")}</span>
        <span className="experience-toggle-main">
          <span className="experience-card-meta">{caseStudy.group} <b>{caseStudy.period}</b></span>
          <strong>{caseStudy.headline}</strong>
          <em>{caseStudy.recruiterSummary}</em>
          {primaryOutcome && <span className="experience-quick-proof"><b>{primaryOutcome.value}</b><span>{primaryOutcome.label}</span></span>}
        </span>
        <ChevronDown size={20} />
      </button>

      {expanded && (
        <div className="experience-detail" id={panelId}>
          <section className="experience-ownership" aria-label={`Omer's role in ${caseStudy.headline}`}>
            <div>
              <span className="experience-detail-kicker">My role</span>
              <p>{caseStudy.ownership}</p>
            </div>
            <div className="experience-stakeholders" aria-label="Key stakeholders">
              <span>Teams involved</span>
              <div>{caseStudy.stakeholders.map((stakeholder) => <b key={stakeholder}>{stakeholder}</b>)}</div>
            </div>
          </section>

          <section className="experience-actions">
            <h3>What I did</h3>
            <ul>
              {caseStudy.whatIDid.map((item) => <li key={item}><CheckCircle2 size={15} /><span>{item}</span></li>)}
            </ul>
          </section>

          <section className="experience-evidence-work">
            <div className="experience-evidence">
              <h3>Evidence</h3>
              <div>
                {caseStudy.outcomes.map((outcome) => (
                  <article key={`${outcome.value}-${outcome.label}`}>
                    <strong>{outcome.value}</strong>
                    <span>{outcome.label}</span>
                    <small>{outcome.scope}</small>
                  </article>
                ))}
              </div>
            </div>

            <div className="experience-workstreams">
              <h3>Workstreams in scope</h3>
              <div>
                {caseStudy.workstreams.map((workstream) => (
                  <article key={workstream.id}>
                    <strong>{workstream.title}</strong>
                    <p>{workstream.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {caseStudy.references.length > 0 && (
            <div className="experience-links">
              <span>Selected public work</span>
              <div>
                {caseStudy.references.map((link) => (
                  <a key={link.url} href={link.url} target="_blank" rel="noreferrer">
                    <strong>{link.label}<ExternalLink size={12} /></strong>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export function ExperiencePage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const career = experienceDossier.career;
  return (
    <section className="page-view experience-page">
      <header className="experience-hero">
        <div className="experience-hero-copy">
          <span className="eyebrow">{career.eyebrow}</span>
          <h1>{career.title}</h1>
          <p>{career.lead}</p>
        </div>
      </header>

      <section className="experience-role-rail" aria-label="Cloudflare role progression">
        {career.roles.map((role) => (
          <article key={role.period}>
            <span>{role.period}</span>
            <h2>{role.title}</h2>
            <p>{role.description}</p>
          </article>
        ))}
      </section>

      <section className="experience-outcomes" aria-labelledby="experience-outcomes-title">
        <div className="experience-outcomes-heading">
          <span className="section-kicker">{career.outcomes.eyebrow}</span>
          <h2 id="experience-outcomes-title">{career.outcomes.title}</h2>
          <p>{career.outcomes.lead}</p>
        </div>
        <div className="experience-metrics" aria-label="Selected outcomes from Omer's Cloudflare leadership period">
          {career.metrics.map((metric) => (
            <article key={`${metric.value}-${metric.label}`}>
              <strong>{metric.value}</strong>
              <span>{metric.label}</span>
              <small>{metric.note}</small>
            </article>
          ))}
        </div>
      </section>

      <div className="experience-dossier-heading">
        <span className="section-kicker">Selected work at Cloudflare</span>
        <h2>Seven case studies, built from 16 products and programs.</h2>
        <p>Each case starts with my role and evidence. Product detail appears only when it helps explain the work.</p>
      </div>
      <div className="experience-accordion">
        {experienceDossier.caseStudies.map((caseStudy, index) => (
          <ExperienceCaseStudyCard
            key={caseStudy.id}
            caseStudy={caseStudy}
            index={index}
            expanded={openId === caseStudy.id}
            onToggle={() => setOpenId((current) => current === caseStudy.id ? null : caseStudy.id)}
          />
        ))}
      </div>
    </section>
  );
}

export function InsightsPage({ componentAiEnabled }: { componentAiEnabled: boolean }) {
  const [insightDeck, setInsightDeck] = useState(() => ({ cards: shuffleItems(keyInsightCards), offset: 0 }));
  const [quoteDeck] = useState(() => shuffleItems(colleagueQuotes));
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [isQuotePaused, setIsQuotePaused] = useState(false);
  const [quoteElapsedMs, setQuoteElapsedMs] = useState(0);
  const [quoteProgressRun, setQuoteProgressRun] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [insightClickCount, setInsightClickCount] = useState(0);
  const [generatedCards, setGeneratedCards] = useState<InsightCard[] | null>(null);
  const [revision, setRevision] = useState(0);
  const recentInsightTitles = useRef<string[]>([]);
  const insightInitialRequested = useRef(false);
  const quoteProgressStartedAt = useRef(Date.now());
  const activeQuote = quoteDeck[quoteIndex] ?? colleagueQuotes[0];
  const insightLimitReached = insightClickCount >= INSIGHT_CLICK_LIMIT;
  const orderedCards = useMemo(
    () => generatedCards ?? getInsightWindow(insightDeck.cards, insightDeck.offset),
    [generatedCards, insightDeck],
  );
  const advanceStaticInsights = useCallback(() => {
    setGeneratedCards(null);
    setInsightDeck((current) => {
      const nextOffset = current.offset + INSIGHT_CARD_COUNT;
      if (nextOffset + INSIGHT_CARD_COUNT <= current.cards.length) return { ...current, offset: nextOffset };
      return { cards: shuffleItems(keyInsightCards), offset: 0 };
    });
  }, []);
  const resetQuoteProgress = useCallback(() => {
    quoteProgressStartedAt.current = Date.now();
    setQuoteElapsedMs(0);
    setQuoteProgressRun((current) => current + 1);
  }, []);
  const rotateQuote = useCallback((direction: 1 | -1) => {
    resetQuoteProgress();
    setQuoteIndex((current) => (current + direction + quoteDeck.length) % quoteDeck.length);
  }, [quoteDeck.length, resetQuoteProgress]);
  const pauseQuote = useCallback(() => {
    if (isQuotePaused) return;
    const elapsed = Math.min(QUOTE_ROTATION_MS, quoteElapsedMs + Date.now() - quoteProgressStartedAt.current);
    setQuoteElapsedMs(elapsed);
    setIsQuotePaused(true);
  }, [isQuotePaused, quoteElapsedMs]);
  const resumeQuote = useCallback(() => {
    if (!isQuotePaused) return;
    quoteProgressStartedAt.current = Date.now();
    setIsQuotePaused(false);
  }, [isQuotePaused]);
  const refreshInsights = async (source: "initial" | "manual" = "manual") => {
    if (isRefreshing || (source === "manual" && insightLimitReached)) return;
    const startedAt = Date.now();
    if (source === "manual") setInsightClickCount((value) => Math.min(INSIGHT_CLICK_LIMIT, value + 1));
    setIsRefreshing(true);
    const visibleTitles = orderedCards.map((card) => card.title);
    const recentTitles = [...new Set([...recentInsightTitles.current, ...visibleTitles])].slice(-15);
    try {
      if (componentAiEnabled) {
        try {
          const generated = await generateAiComponent<{ items: InsightCard[] }>("insights", buildInsightPrompt(recentTitles));
          const freshItems = keepFreshInsights(generated.items, recentTitles);
          recentInsightTitles.current = [...recentTitles, ...freshItems.map((card) => card.title)].slice(-24);
          await waitForMinimumLoading(startedAt);
          setGeneratedCards(freshItems);
        } catch {
          await waitForMinimumLoading(startedAt);
          setGeneratedCards(null);
          advanceStaticInsights();
        }
      } else {
        await waitForMinimumLoading(startedAt);
        advanceStaticInsights();
      }
      setRevision((value) => value + 1);
    } finally {
      setIsRefreshing(false);
    }
  };
  useEffect(() => {
    if (!componentAiEnabled || insightInitialRequested.current) return;
    insightInitialRequested.current = true;
    void refreshInsights("initial");
  }, [componentAiEnabled]);
  useEffect(() => {
    if (isQuotePaused) return;
    const remaining = Math.max(0, QUOTE_ROTATION_MS - quoteElapsedMs);
    const timer = window.setTimeout(() => rotateQuote(1), remaining);
    return () => window.clearTimeout(timer);
  }, [isQuotePaused, quoteElapsedMs, quoteIndex, rotateQuote]);

  return (
    <section className="page-view insights-page">
      <header className="page-heading">
        <span className="eyebrow">Career highlights</span>
        <h1>Key Insights.</h1>
        <p>A concise view of the scale, influence, market recognition, and leadership qualities Omer brings to ambitious product roles.</p>
      </header>

      <div className="insights-content">
        <section id="key-insights" className="insights-hero-card anchor-target" aria-labelledby="insights-title">
          <div className="section-heading section-heading--compact">
            <div>
              <span className="section-kicker">Why Omer stands out</span>
              <h2 id="insights-title">Key Insights</h2>
            </div>
            <button type="button" className="regenerate-button" onClick={() => void refreshInsights("manual")} disabled={isRefreshing || insightLimitReached} aria-busy={isRefreshing} aria-describedby={insightLimitReached ? "insight-limit-note" : undefined}>
              {isRefreshing
                ? <ThinkingStatus compact />
                : <><Sparkles size={16} /> {insightLimitReached ? "Five insight sets generated" : componentAiEnabled ? "Generate new insights" : "Show more insights"}</>}
            </button>
          </div>
          {insightLimitReached && <p className="insight-limit-note" id="insight-limit-note">If more than five are not enough, <Link to="/experience">check out the experience</Link> and <Link to="/footprint">footprint</Link>.</p>}
          <div className="insights-grid" aria-live="polite" aria-busy={isRefreshing}>
            {(isRefreshing ? Array.from({ length: 3 }, () => null) : orderedCards).map((card, index) => (
              <article className="insight-card insight-card--page" key={card ? `${revision}-${card.title}-${index}` : `loading-${index}`}>
                <div className="insight-topline">
                  <span>Insight</span>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                </div>
                {card
                  ? generatedCards
                    ? <SimulatedInsightContent title={card.title} description={card.description} />
                    : <><h2>{card.title}</h2><p>{card.description}</p></>
                  : <div className="insight-ai-loading" aria-label="Generating AI insight"><ThinkingStatus className="insight-thinking-status" /></div>}
              </article>
            ))}
          </div>
        </section>

        <section id="quotes" className="quote-feature-card anchor-target" aria-labelledby="colleague-quotes-title">
          <div className="quote-feature-heading">
            <div>
              <span className="section-kicker">Trusted by teams</span>
              <h2 id="colleague-quotes-title">Colleague &amp; Manager Quotes</h2>
            </div>
          </div>
          <div className="quote-feature-body">
            <button type="button" className="quote-nav quote-nav--previous" onClick={() => rotateQuote(-1)} aria-label="Previous quote" title="Previous quote">
              <ChevronLeft size={24} />
            </button>
            <figure className="quote-slide" key={`${quoteIndex}-${quoteProgressRun}`}>
              <blockquote>“{activeQuote.quote}”</blockquote>
              <figcaption>- {activeQuote.attribution}</figcaption>
              <div className={`quote-progress${isQuotePaused ? " quote-progress--paused" : ""}`} aria-hidden="true"><span /></div>
              <button
                type="button"
                className="quote-pause-control"
                onClick={isQuotePaused ? resumeQuote : pauseQuote}
                aria-label={isQuotePaused ? "Resume quote rotation" : "Pause quote rotation"}
                aria-pressed={isQuotePaused}
              >
                {isQuotePaused ? <><Play size={13} /> Play</> : <><Pause size={13} /> Pause</>}
              </button>
            </figure>
            <button type="button" className="quote-nav quote-nav--next" onClick={() => rotateQuote(1)} aria-label="Next quote" title="Next quote">
              <ChevronRight size={24} />
            </button>
          </div>
        </section>
      </div>
    </section>
  );
}

type FootprintItem = { id: string; sourceType: string; title: string; url: string; summary?: string; publishedAt?: string };
type FootprintInsight = { sourceType: string; outlet: string; date: string; title: string; summary: string; insights: string[] };
type SpeakingPhoto = { src: string; width: number; height: number; title: string; location: string; year: string; alt: string };

const speakingImageWidths = [480, 768, 1080, 1280] as const;
const speakingThumbWidths = [120, 180, 240] as const;
const speakingPhotoHostPattern = /(^|\.)omerisbusy\.com$/i;
const SHOW_SPEAKING_PHOTOS = false;

function shouldUseCloudflareImageTransforms() {
  return import.meta.env.VITE_USE_CLOUDFLARE_IMAGE_TRANSFORMS === "true" && typeof window !== "undefined" && speakingPhotoHostPattern.test(window.location.hostname);
}

function speakingPhotoSlug(src: string) {
  const fileName = src.split("/").pop() ?? "";
  const extensionIndex = fileName.lastIndexOf(".");
  return extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName;
}

function cloudflareSpeakingPhotoPath(src: string, width: number, quality: number) {
  const sourcePath = src.replace(/^\/+/, "");
  return `/cdn-cgi/image/width=${width},quality=${quality},format=auto,fit=scale-down,metadata=none/${sourcePath}`;
}

function localSpeakingPhotoPath(photo: SpeakingPhoto, width: number) {
  return `/conference-photos/generated/${speakingPhotoSlug(photo.src)}-${width}.jpg`;
}

function speakingPhotoPath(photo: SpeakingPhoto, width: number, quality: number) {
  return shouldUseCloudflareImageTransforms() ? cloudflareSpeakingPhotoPath(photo.src, width, quality) : localSpeakingPhotoPath(photo, width);
}

function speakingPhotoSrcSet(photo: SpeakingPhoto, widths: readonly number[], quality: number) {
  return widths.map((width) => `${speakingPhotoPath(photo, width, quality)} ${width}w`).join(", ");
}

const latestMediaInsight = {
  sourceType: "Writing, media, and speaking",
  outlet: "Public impact",
  date: "Multi-year view",
  title: "A product leader who can shape the story as well as the strategy.",
  summary: "Omer pairs technical product leadership with unusual communication reach. Sixty-five Cloudflare blogs reached over 1.3 million unique readers, were translated into more than 15 languages, and helped generate more than 1,800 known media articles. He has also spoken at more than 30 events, including CISA JCDC ATTE, WebSummit London, GameCon Germany, NANOG 88 Seattle, Nordic Cyber Summit, CIO Nordics in Helsinki, HackCon in Oslo, Cloudflare Customer Advisory Board in Sydney, and JISC.",
  insights: [
    "His writing makes complex cybersecurity events useful to customers, journalists, and industry decision-makers.",
    "Global translation and more than 1,800 known media articles extended Omer’s ideas far beyond their original publication.",
    "Speaking at industry forums across government, network operations, cybersecurity, customer advisory, and developer audiences reinforced his reputation as a clear and credible technical communicator.",
  ],
};

const speakingPhotos: SpeakingPhoto[] = [
  { src: "/conference-photos/gamechanger-tlv-2026-stage.png", width: 1800, height: 1350, title: "GameChanger TLV", location: "Tel Aviv", year: "2026", alt: "Omer presenting on the GameChanger TLV stage in front of a Cloudflare mobile gaming slide." },
  { src: "/conference-photos/gamechanger-tlv-2026-wide.jpg", width: 1800, height: 1350, title: "GameChanger TLV", location: "Tel Aviv", year: "2026", alt: "Wide auditorium view of Omer presenting at GameChanger TLV." },
  { src: "/conference-photos/tau-iucc-2025-a.jpg", width: 1800, height: 1350, title: "Inter-University Computation Center", location: "Tel Aviv University", year: "2025", alt: "Omer speaking at Tel Aviv University beside a DDoS traffic slide." },
  { src: "/conference-photos/tau-iucc-2025.jpg", width: 1800, height: 1350, title: "Inter-University Computation Center", location: "Tel Aviv University", year: "2025", alt: "Omer presenting DDoS data to an audience at Tel Aviv University." },
  { src: "/conference-photos/israelis-in-tech-2025.jpg", width: 1800, height: 1350, title: "Israelis in Tech meetup", location: "London", year: "2025", alt: "Omer hosting an Israelis in Tech meetup in a decorated office event space." },
  { src: "/conference-photos/house-of-lords-2024.jpg", width: 1800, height: 1350, title: "Government briefing", location: "House of Lords", year: "2024", alt: "Omer presenting DDoS trends during a briefing at the House of Lords." },
  { src: "/conference-photos/nordics-2024-main-stage.jpg", width: 1800, height: 1350, title: "Nordic Cyber Summit", location: "Helsinki", year: "2024", alt: "Omer presenting on generative AI and cyber warfare capabilities at a Nordic event." },
  { src: "/conference-photos/nordics-2024-talk.jpg", width: 1350, height: 1800, title: "Nordic Cyber Summit", location: "Helsinki", year: "2024", alt: "Omer presenting a machine learning slide at Nordic Cyber Summit." },
  { src: "/conference-photos/cio-nordics-helsinki.jpg", width: 1350, height: 1800, title: "CIO Nordics", location: "Helsinki", year: "2024", alt: "Omer speaking on stage during a CIO Nordics panel in Helsinki." },
  { src: "/conference-photos/helsinki-2024-wide.jpg", width: 1800, height: 1350, title: "Cybersecurity forum", location: "Helsinki", year: "2024", alt: "Wide room view of Omer presenting to a large cybersecurity audience in Helsinki." },
  { src: "/conference-photos/helsinki-2024-room.jpg", width: 1350, height: 1800, title: "Cybersecurity forum", location: "Helsinki", year: "2024", alt: "Audience view of Omer presenting a cybersecurity framework slide in Helsinki." },
  { src: "/conference-photos/websummit-london-2023.jpg", width: 1800, height: 1196, title: "Web Summit", location: "London", year: "2023", alt: "Omer presenting at the Cloudflare stand during Web Summit London." },
  { src: "/conference-photos/barcelona-cybersecurity-congress-2023-stage.jpg", width: 1350, height: 1800, title: "Barcelona Cybersecurity Congress", location: "Barcelona", year: "2023", alt: "Omer in a fireside chat on the Barcelona Cybersecurity Congress stage." },
  { src: "/conference-photos/barcelona-cybersecurity-conference-2023-talk.jpg", width: 1350, height: 1800, title: "Barcelona Cybersecurity Congress", location: "Barcelona", year: "2023", alt: "Omer speaking with a microphone on the Barcelona Cybersecurity Congress stage." },
  { src: "/conference-photos/barcelona-cybersecurity-conference-2023-room.jpg", width: 1800, height: 1350, title: "Barcelona Cybersecurity Congress", location: "Barcelona", year: "2023", alt: "Wide audience view during Omer's Barcelona Cybersecurity Congress session." },
  { src: "/conference-photos/london-2022-workshop.jpg", width: 1800, height: 1350, title: "Security workshop", location: "London", year: "2022", alt: "Omer presenting in a classroom-style security workshop in London." },
  { src: "/conference-photos/copenhagen-2022.jpg", width: 1800, height: 1648, title: "DDoS protection briefing", location: "Copenhagen", year: "2022", alt: "Omer presenting Cloudflare DDoS protection material in Copenhagen." },
  { src: "/conference-photos/dublin-isf-2019.jpg", width: 1800, height: 1216, title: "ISF Annual World Congress", location: "Dublin", year: "2019", alt: "Omer presenting DDoS Analytics at Cloudflare during the ISF Annual World Congress in Dublin." },
];

export function FootprintPage({ componentAiEnabled }: { componentAiEnabled: boolean }) {
  const [items, setItems] = useState<FootprintItem[]>([]);
  const [mediaInsight, setMediaInsight] = useState<FootprintInsight>(latestMediaInsight);
  const [isMediaInsightLoading, setIsMediaInsightLoading] = useState(componentAiEnabled);
  const [speakingPhotoIndex, setSpeakingPhotoIndex] = useState(0);
  const mediaInsightRequest = useRef<Promise<FootprintInsight> | null>(null);
  const activeSpeakingPhoto = speakingPhotos[speakingPhotoIndex] ?? speakingPhotos[0];
  const rotateSpeakingPhoto = useCallback((direction: 1 | -1) => {
    setSpeakingPhotoIndex((current) => (current + direction + speakingPhotos.length) % speakingPhotos.length);
  }, []);
  useEffect(() => { void fetch("/api/footprint").then((response) => response.ok ? response.json<{ items: FootprintItem[] }>() : { items: [] }).then((data) => setItems(data.items)); }, []);
  useEffect(() => {
    if (!componentAiEnabled) {
      mediaInsightRequest.current = null;
      setIsMediaInsightLoading(false);
      return;
    }

    let active = true;
    const startedAt = Date.now();
    setIsMediaInsightLoading(true);
    const request = mediaInsightRequest.current ?? generateAiComponent<FootprintInsight>("footprint", "Synthesize the strongest multi-year public footprint for an executive audience.");
    mediaInsightRequest.current = request;

    void (async () => {
      try {
        const generated = await request;
        await waitForMinimumLoading(startedAt);
        if (active) setMediaInsight({ ...latestMediaInsight, ...generated });
      } catch {
        await waitForMinimumLoading(startedAt);
      } finally {
        if (active) setIsMediaInsightLoading(false);
      }
    })();

    return () => { active = false; };
  }, [componentAiEnabled]);
  useEffect(() => {
    if (!SHOW_SPEAKING_PHOTOS) return;
    const nextPhotos = [
      speakingPhotos[(speakingPhotoIndex + 1) % speakingPhotos.length],
      speakingPhotos[(speakingPhotoIndex - 1 + speakingPhotos.length) % speakingPhotos.length],
    ].filter(Boolean);
    nextPhotos.forEach((photo) => {
      const image = new Image();
      image.decoding = "async";
      image.src = speakingPhotoPath(photo, 768, 82);
    });
  }, [speakingPhotoIndex]);
  return (
    <section className="page-view">
      <header className="page-heading"><span className="eyebrow">{mediaSnapshot.eyebrow}</span><h1>{mediaSnapshot.title}</h1><p>{mediaSnapshot.summary}</p></header>
      <section className={`media-summary-card${isMediaInsightLoading ? " media-summary-card--loading" : ""}`} aria-label="Why it matters" aria-live="polite" aria-busy={isMediaInsightLoading}>
        <div className="media-summary-topline">
          <span><Sparkles size={14} /> Why it matters</span>
          <span>{mediaInsight.date}</span>
        </div>
        {isMediaInsightLoading
          ? <div className="media-summary-loading"><ThinkingStatus className="media-summary-thinking-status" /></div>
          : <div className="media-summary-grid">
            <div>
              <span className="media-summary-kicker">{mediaInsight.outlet} · {mediaInsight.sourceType}</span>
              <h2 id="latest-media-title">{mediaInsight.title}</h2>
              <p>{mediaInsight.summary}</p>
            </div>
            <div className="media-insight-list">
              {mediaInsight.insights.map((item) => <div key={item}><CheckCircle2 size={16} /><span>{item}</span></div>)}
            </div>
          </div>
        }
      </section>
      {SHOW_SPEAKING_PHOTOS && <section className="speaking-carousel-card" aria-labelledby="speaking-carousel-title">
        <div className="section-heading section-heading--compact">
          <div>
            <span className="section-kicker">Rooms, stages, briefings</span>
            <h2 id="speaking-carousel-title">Speaking in rooms where technical trust matters</h2>
          </div>
          <p>A visual record of conferences, government briefings, community events, and practitioner forums across Europe and Israel.</p>
        </div>
        <div className="speaking-carousel-shell">
          <button type="button" className="speaking-nav speaking-nav--previous" onClick={() => rotateSpeakingPhoto(-1)} aria-label="Previous speaking photo" title="Previous photo">
            <ChevronLeft size={24} />
          </button>
          <figure className="speaking-slide" key={activeSpeakingPhoto.src}>
            <div className="speaking-image-wrap">
              <img
                src={speakingPhotoPath(activeSpeakingPhoto, 1080, 82)}
                srcSet={speakingPhotoSrcSet(activeSpeakingPhoto, speakingImageWidths, 82)}
                sizes="(max-width: 520px) calc(100vw - 44px), (max-width: 900px) calc(100vw - 164px), 980px"
                alt={activeSpeakingPhoto.alt}
                width={activeSpeakingPhoto.width}
                height={activeSpeakingPhoto.height}
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </div>
            <figcaption>
              <span><Camera size={14} /> {activeSpeakingPhoto.year}</span>
              <strong>{activeSpeakingPhoto.title}</strong>
              <em>{activeSpeakingPhoto.location}</em>
            </figcaption>
          </figure>
          <button type="button" className="speaking-nav speaking-nav--next" onClick={() => rotateSpeakingPhoto(1)} aria-label="Next speaking photo" title="Next photo">
            <ChevronRight size={24} />
          </button>
        </div>
        <div className="speaking-thumb-rail" aria-label="Choose speaking photo">
          {speakingPhotos.map((photo, index) => (
            <button
              type="button"
              key={photo.src}
              className={index === speakingPhotoIndex ? "selected" : undefined}
              onClick={() => setSpeakingPhotoIndex(index)}
              aria-label={`Show ${photo.title}, ${photo.location}, ${photo.year}`}
              aria-current={index === speakingPhotoIndex ? "true" : undefined}
            >
              <img
                src={speakingPhotoPath(photo, 180, 70)}
                srcSet={speakingPhotoSrcSet(photo, speakingThumbWidths, 70)}
                sizes="(max-width: 520px) 72px, 82px"
                alt=""
                width={photo.width}
                height={photo.height}
                loading={index < 6 ? "eager" : "lazy"}
                decoding="async"
                fetchPriority="low"
              />
            </button>
          ))}
        </div>
      </section>}
      <section className="media-proof-card" aria-labelledby="media-proof-title">
        <div className="section-heading section-heading--compact">
          <div>
            <span className="section-kicker">Reach and influence</span>
            <h2 id="media-proof-title">Impact beyond the product</h2>
          </div>
          <p>Writing, research, and public appearances that carried Omer’s technical perspective to customers, practitioners, policymakers, and industry audiences worldwide.</p>
        </div>
        <div className="media-proof-grid">
          {mediaStats.map((stat) => (
            <article key={stat.label} className="media-proof-stat">
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
              <p>{stat.note}</p>
              {stat.url && <a className="media-proof-link" href={stat.url} target="_blank" rel="noreferrer">{stat.linkLabel}<ExternalLink size={12} /></a>}
            </article>
          ))}
        </div>
      </section>
      <section className="media-story-grid" aria-labelledby="media-story-title">
        <div className="section-heading section-heading--compact">
          <div>
            <span className="section-kicker">Selected achievements</span>
            <h2 id="media-story-title">How Omer’s work travelled</h2>
          </div>
          <p>A few examples of reach, technical authority, and global relevance.</p>
        </div>
        <div className="media-story-list">
          {mediaSnippets.map((snippet) => (
            <article key={snippet.title} className="media-story-card">
              <h3>{snippet.title}</h3>
              <p>{snippet.summary}</p>
            </article>
          ))}
        </div>
      </section>
      {items.length > 0 && <div className="footprint-list">{items.map((item) => <a key={item.id} href={item.url} target="_blank" rel="noreferrer"><span>{item.sourceType}</span><div><h2>{item.title}</h2>{item.summary && <p>{item.summary}</p>}</div><ExternalLink size={18} /></a>)}</div>}
    </section>
  );
}

const architectureNodes = [
  { icon: Code2, title: "React + Worker", tag: "Interface & API", text: "One deployable unit with static assets and focused API routes." },
  { icon: ShieldCheck, title: "Security edge", tag: "WAF · Turnstile", text: "Challenges, origin checks, rate limits, and strict browser policy." },
  { icon: BrainCircuit, title: "Protected AI", tag: "Workers AI", text: "Small, focused prompts with no arbitrary tools or private data access." },
  { icon: Network, title: "Retrieval", tag: "Vectorize", text: "Only reviewed public material enters the model context." },
  { icon: Database, title: "Private state", tag: "D1 · Durable Objects", text: "Time-limited records, contact storage, quotas, and cost coordination." },
  { icon: Gauge, title: "Cost control", tag: "AI Gateway", text: "Cloudflare security and AI controls enforce usage and cost limits, caching, and graceful degradation." },
];

const architectureFlow = [
  { title: "Visitor", text: "Prompt, contact, or browse", icon: UserRound },
  { title: "Cloudflare edge", text: "Turnstile, WAF, rate limits", icon: Shield },
  { title: "Protected AI", text: "Retrieval, citations, guardrails", icon: Bot },
  { title: "Stored state", text: "D1, Durable Objects, logs", icon: Database },
] as const;

export function ArchitecturePage() {
  return (
    <section className="page-view architecture-page">
      <header className="page-heading"><span className="eyebrow">System anatomy</span><h1>Security is part of the product.</h1><p>This public view explains design intent without exposing resource identifiers, secrets, private rules, or operational internals.</p></header>
      <div className="architecture-diagram">
        <div className="architecture-diagram-top">
          <div>
            <span className="diagram-kicker">Request path</span>
            <h2>How a visitor question becomes an AI response.</h2>
          </div>
          <p>Each layer is designed to turn a visitor’s question into a useful AI response while protecting privacy, security, and cost.</p>
        </div>
        <div className="architecture-diagram-flow" aria-label="Architecture flow diagram">
          {architectureFlow.map(({ title, text, icon: Icon }, index) => (
            <div className="diagram-step" key={title}>
              <div className="diagram-icon"><Icon size={18} /></div>
              <strong>{title}</strong>
              <span>{text}</span>
              {index < architectureFlow.length - 1 && <div className="diagram-arrow"><ArrowRight size={16} /></div>}
            </div>
          ))}
        </div>
      </div>
      <div className="architecture-map"><div className="architecture-spine"><span>Visitor</span><ArrowRight size={16} /><span>Cloudflare edge</span><ArrowRight size={16} /><span>AI response</span></div><div className="architecture-grid">{architectureNodes.map(({ icon: Icon, title, tag, text }) => <article key={title}><div className="node-icon"><Icon size={20} /></div><span>{tag}</span><h2>{title}</h2><p>{text}</p></article>)}</div></div>
      <div className="security-strip"><div><LockKeyhole size={18} /><span><strong>Fail closed</strong> on missing evidence</span></div><div><Shield size={18} /><span><strong>30-day</strong> raw chat retention</span></div><div><Activity size={18} /><span><strong>Metadata-only</strong> operational logs</span></div></div>
      <div className="architecture-notes"><article><span className="note-number">01</span><h2>Prompt injection</h2><p>Inputs and retrieved sources are untrusted data. The model has no browser, shell, secret access, or write tools. Citation and leak checks run before output is returned.</p></article><article><span className="note-number">02</span><h2>Denial of wallet</h2><p>Turnstile, signed sessions, per-session quotas, request limits, concurrency gates, fixed token bounds, and a global budget gate reduce cost-amplification paths.</p></article><article><span className="note-number">03</span><h2>Graceful failure</h2><p>Core portfolio pages and prepared answers remain available when generation, retrieval, external feeds, or the monthly AI allowance is unavailable.</p></article></div>
    </section>
  );
}

type ContactPageProps = { siteKey: string; theme: "light" | "dark"; visitorCountryCode: string };

export function ContactPage(props: ContactPageProps) {
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  return <ContactForm key={search} {...props} prefill={contactPrefill(searchParams)} />;
}

function ContactForm({ siteKey, theme, visitorCountryCode, prefill }: ContactPageProps & { prefill: ReturnType<typeof contactPrefill> }) {
  const [token, setToken] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState("");
  const [messageLength, setMessageLength] = useState(prefill.message.length);
  const selectedCountryCode = getPhoneCountry(prefill.countryCode || visitorCountryCode || defaultPhoneCountryCode).code;
  const onToken = useCallback((value: string) => setToken(value), []);
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    if (!token) { setError("Please complete the security check."); return; }
    setStatus("sending"); setError(""); setToken("");
    try {
      await submitContact({
        name: String(data.get("name") ?? ""), email: String(data.get("email") ?? ""), phone: String(data.get("phone") ?? "") || undefined,
        company: String(data.get("company") ?? "") || undefined, subject: String(data.get("subject") ?? ""), message: String(data.get("message") ?? ""),
        turnstileToken: token, consent: true,
      });
      setStatus("sent"); form.reset(); setToken(""); setMessageLength(0);
    } catch (caught) {
      setStatus("error"); setError(caught instanceof Error ? caught.message : "The message could not be stored."); setResetKey((value) => value + 1);
    }
  };
  if (status === "sent") return <section className="page-view"><div className="success-state"><CheckCircle2 size={34} /><span className="eyebrow">Stored securely</span><h1>Your message is in Omer’s queue.</h1><p>The enquiry has been saved in Cloudflare and will be retained according to the Privacy Policy.</p><button type="button" className="primary-action" onClick={() => { setStatus("idle"); setMessageLength(prefill.message.length); }}>Send another message</button></div></section>;
  return (
    <section className="page-view contact-page">
      <header className="page-heading"><span className="eyebrow">Start a useful conversation</span><h1>Bring the difficult problem.</h1><p>Share enough context to make the conversation worthwhile. Please do not submit credentials, confidential data, or special-category personal information.</p></header>
      <form className="contact-form" onSubmit={(event) => void onSubmit(event)}>
        <div className="form-grid">
          <label><span className="field-label">Name<span className="required-marker" aria-hidden="true">*</span></span><input name="name" defaultValue={prefill.name} autoComplete="name" minLength={2} maxLength={100} required /></label>
          <label><span className="field-label">Email<span className="required-marker" aria-hidden="true">*</span></span><input name="email" defaultValue={prefill.email} type="email" autoComplete="email" maxLength={254} required /></label>
          <label><span className="field-label">Company</span><input name="company" defaultValue={prefill.company} autoComplete="organization" maxLength={120} /></label>
          <PhoneNumberField name="phone" defaultCountryCode={selectedCountryCode} defaultNationalNumber={prefill.nationalNumber} />
        </div>
        <label><span className="field-label">Subject<span className="required-marker" aria-hidden="true">*</span></span><span className="contact-select"><select name="subject" required defaultValue={prefill.subject}><option value="" disabled>Select a subject</option>{contactSubjects.map((option) => <option key={option}>{option}</option>)}</select><ChevronDown size={16} aria-hidden="true" /></span></label>
        <label><span className="field-label">Message<span className="required-marker" aria-hidden="true">*</span></span><textarea name="message" defaultValue={prefill.message} minLength={20} maxLength={4000} rows={7} required aria-describedby="message-guidance" onChange={(event) => setMessageLength(event.target.value.length)} /><small id="message-guidance" className="form-hint"><span>Minimum 20 characters</span><span>{messageLength.toLocaleString()}/4,000</span></small></label>
        <p className="required-note"><span aria-hidden="true">*</span> Required fields</p>
        <label className="consent-row"><input name="consent" type="checkbox" required /><span>I am 16 or older. I agree to the <Link to="/terms">Terms of Use</Link>, acknowledge the <Link to="/privacy">Privacy Policy</Link>, and consent to Omer storing this information and contacting me about this enquiry. Current versions: Terms {TERMS_VERSION}, Privacy {PRIVACY_VERSION}.</span></label>
        <TurnstileWidget siteKey={siteKey} action="contact" theme={theme} onToken={onToken} resetKey={resetKey} />
        {error && <p className="form-error">{error}</p>}
        <button className="primary-action submit-contact" type="submit" disabled={status === "sending" || !token}>{status === "sending" ? "Storing securely…" : <>Send securely <Send size={16} /></>}</button>
      </form>
    </section>
  );
}

const resumePromptPresets = [
  "Create Omer's resume for a Product Leader role.",
  "Redo this for a gaming AI Senior Product Manager role.",
  "Tailor this for a cybersecurity product leadership role with executive impact.",
  "Make it concise, recruiter-friendly, and keyword rich.",
] as const;
const RESUME_PROMPT_MAX_CHARS = 8_000;

const workPreferenceCopy = [
  "Omer prefers a balanced hybrid setup with room for in-person collaboration, whiteboarding, and kitchen conversations that spark good product ideas.",
  "Hybrid works well. It keeps the flexibility of focused work while leaving space for office days, team energy, and the occasional onsite session.",
  "This still fits as a hybrid setup. Omer can flex around travel, customer meetings, conferences, and team onsites when the role calls for it.",
  "A hybrid rhythm is the sweet spot. It leaves enough office time for teamwork while preserving flexibility for travel, focused work, and time with Odin.",
  "More days in the office are possible in the right role. Some flexibility helps balance conferences, customer meetings, team events, and life with Odin, with trusted dog sitters available when needed.",
  "A full week in the office is the least aligned with Omer’s preference. He values face-to-face collaboration, but prefers flexibility around travel, customer and team commitments, and Odin. He can still adapt for the right opportunity.",
] as const;

type ResumeExportFormat = "pdf" | "docx" | "markdown" | "plaintext";

function sanitizeFilename(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

function buildResumeMarkdown(draft: ResumeDraft): string {
  const lines = draft.body.split("\n");
  const [name, subtitle, ...rest] = lines;
  return [`# ${name}`, `## ${subtitle}`, ...rest].join("\n");
}

function buildResumePlaintext(draft: ResumeDraft): string {
  return draft.body.trim();
}

function isAllCapsHeading(line: string): boolean {
  return /^[A-Z][A-Z &/.-]{2,}$/.test(line.trim());
}

async function exportResumeFile(draft: ResumeDraft, format: ResumeExportFormat): Promise<void> {
  const slug = sanitizeFilename(draft.subtitle);
  const baseName = `omer-yoachimik-resume-${slug}`;
  if (format === "markdown") {
    downloadBlob(new Blob([buildResumeMarkdown(draft)], { type: "text/markdown;charset=utf-8" }), `${baseName}.md`);
    return;
  }
  if (format === "plaintext") {
    downloadBlob(new Blob([buildResumePlaintext(draft)], { type: "text/plain;charset=utf-8" }), `${baseName}.txt`);
    return;
  }
  if (format === "docx") {
    const { Document, HeadingLevel, Packer, Paragraph, TextRun } = await import("docx");
    const paragraphs = draft.body.split("\n").flatMap((line, index, lines) => {
      if (!line.trim()) return [new Paragraph({ text: "" })];
      if (index === 0) return [new Paragraph({ children: [new TextRun({ text: line, bold: true, size: 34 })] })];
      if (index === 1) return [new Paragraph({ children: [new TextRun({ text: line, italics: true, size: 22 })] })];
      if (isAllCapsHeading(line)) return [new Paragraph({ text: line, heading: HeadingLevel.HEADING_2 })];
      if (line.trim().startsWith("• ")) return [new Paragraph({ text: line.replace(/^•\s*/, ""), bullet: { level: 0 } })];
      return [new Paragraph({ text: line })];
    });
    const doc = new Document({
      sections: [{
        properties: {},
        children: paragraphs,
      }],
    });
    const blob = await Packer.toBlob(doc);
    downloadBlob(blob, `${baseName}.docx`);
    return;
  }
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const marginX = 48;
  let cursorY = 56;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const usableWidth = pageWidth - marginX * 2;
  const lineHeight = 18;

  const ensureSpace = (height = lineHeight) => {
    if (cursorY + height <= pdf.internal.pageSize.getHeight() - 48) return;
    pdf.addPage();
    cursorY = 56;
  };
  const writeLine = (text: string, opts: { size?: number; bold?: boolean; italic?: boolean; gapBefore?: number; gapAfter?: number } = {}) => {
    if (!text.trim()) {
      cursorY += opts.gapAfter ?? 10;
      return;
    }
    const size = opts.size ?? 11.5;
    pdf.setFont("helvetica", opts.bold ? "bold" : opts.italic ? "italic" : "normal");
    pdf.setFontSize(size);
    const wrapped = pdf.splitTextToSize(text, usableWidth) as string[];
    ensureSpace((wrapped.length * (size * 1.35)) + (opts.gapBefore ?? 0) + (opts.gapAfter ?? 0));
    if (opts.gapBefore) cursorY += opts.gapBefore;
    pdf.text(wrapped, marginX, cursorY, { maxWidth: usableWidth });
    cursorY += wrapped.length * (size * 1.35) + (opts.gapAfter ?? 0);
  };

  const lines = draft.body.split("\n");
  lines.forEach((line, index) => {
    if (index === 0) {
      writeLine(line, { size: 20, bold: true, gapAfter: 4 });
      return;
    }
    if (index === 1) {
      writeLine(line, { size: 13, italic: true, gapAfter: 14 });
      return;
    }
    if (!line.trim()) {
      cursorY += 8;
      return;
    }
    if (isAllCapsHeading(line)) {
      writeLine(line, { size: 11.5, bold: true, gapBefore: 6, gapAfter: 4 });
      return;
    }
    const isBullet = line.trim().startsWith("• ");
    writeLine(isBullet ? `- ${line.trim().slice(2)}` : line, { size: 11.2, gapAfter: 2 });
  });
  pdf.save(`${baseName}.pdf`);
}

export function ResumePage({ componentAiEnabled }: { componentAiEnabled: boolean }) {
  const [prompt, setPrompt] = useState<string>(resumePromptPresets[0]);
  const [draft, setDraft] = useState<ResumeDraft | null>(() => buildResumeDraft(resumePromptPresets[0]));
  const [hasGeneratedDraft, setHasGeneratedDraft] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState("");
  const [officeDays, setOfficeDays] = useState(3);
  const [odinOpen, setOdinOpen] = useState(false);
  const [odinIndex, setOdinIndex] = useState(0);
  const [exporting, setExporting] = useState<ResumeExportFormat | null>(null);
  const [exportError, setExportError] = useState("");
  const odinTouchStart = useRef<number | null>(null);

  const recommendation = useMemo(() => workPreferenceCopy[officeDays] ?? workPreferenceCopy[3], [officeDays]);

  const showPreviousOdinPhoto = useCallback(() => {
    setOdinIndex((current) => (current - 1 + odinGallery.length) % odinGallery.length);
  }, []);

  const showNextOdinPhoto = useCallback(() => {
    setOdinIndex((current) => (current + 1) % odinGallery.length);
  }, []);

  useEffect(() => {
    if (!odinOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOdinOpen(false);
      if (event.key === "ArrowLeft") showPreviousOdinPhoto();
      if (event.key === "ArrowRight") showNextOdinPhoto();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [odinOpen, showNextOdinPhoto, showPreviousOdinPhoto]);

  const applyPreset = useCallback((value: string) => {
    setPrompt(value);
  }, []);

  const generateResume = useCallback(async () => {
    if (!prompt.trim() || generating) return;
    setGenerating(true);
    setGenerationError("");
    try {
      const next = componentAiEnabled
        ? await generateAiComponent<ResumeDraft>("resume", prompt.trim())
        : buildResumeDraft(prompt.trim());
      setDraft(next);
      setHasGeneratedDraft(true);
    } catch (error) {
      setGenerationError(error instanceof Error ? error.message : "The resume could not be generated safely.");
    } finally {
      setGenerating(false);
    }
  }, [componentAiEnabled, generating, prompt]);

  const exportResume = useCallback(async (format: ResumeExportFormat) => {
    if (!draft || generating || exporting) return;
    setExporting(format);
    setExportError("");
    try {
      await exportResumeFile(draft, format);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "The resume export could not be created.");
    } finally {
      setExporting(null);
    }
  }, [draft, exporting, generating]);

  const renderOdinPicture = useCallback((slug: string, alt: string) => {
    const srcSet = odinImageWidths.map((width) => `${odinImagePath(slug, width)} ${width}w`).join(", ");
    return (
      <img
        src={odinImagePath(slug, 960)}
        srcSet={srcSet}
        sizes="(max-width: 780px) 94vw, 760px"
        alt={alt}
        loading="eager"
        decoding="async"
      />
    );
  }, []);

  const renderWorkPreference = useCallback((copy: string) => {
    const [beforeOdin, afterOdin] = copy.split("Odin");
    if (afterOdin === undefined) return copy;
    return <>{beforeOdin}<button type="button" className="odin-inline-link" onClick={() => { setOdinIndex(0); setOdinOpen(true); }}>Odin</button>{afterOdin}</>;
  }, []);

  const currentOdinPhoto = odinGallery[odinIndex] ?? odinGallery[0];

  return (
    <section className="page-view resume-page">
      <header className="page-heading">
        <span className="eyebrow">Resume builder</span>
        <h1>Generate Omer’s resume.</h1>
        <p>Download Omer’s current public resume, or tailor it to a specific opportunity. Updated October 2026.</p>
      </header>

      <div className="resume-grid">
        <section className="resume-generator">
          <div className="resume-generator-topline">
            <div>
              <span className="section-kicker">Omer’s resume</span>
              <h2>Customize Omer’s resume</h2>
            </div>
            <span className="resume-badge"><Sparkles size={14} /> AI-assisted</span>
          </div>

          <div className="prompt-presets">
            {resumePromptPresets.map((value) => <button key={value} type="button" onClick={() => applyPreset(value)}>{value}</button>)}
          </div>

          <form className="resume-prompt-box" onSubmit={(event) => { event.preventDefault(); void generateResume(); }}>
            <label className="sr-only" htmlFor="resume-prompt">Resume prompt</label>
            <textarea
              id="resume-prompt"
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              rows={4}
              maxLength={RESUME_PROMPT_MAX_CHARS}
              aria-describedby="resume-prompt-guidance"
              placeholder="Paste a public HTTPS job ad URL, a role description, or tell me how to tailor Omer’s resume..."
            />
            <div className="resume-prompt-actions">
              <span id="resume-prompt-guidance">Try a preset, paste a public job ad URL, or add up to {RESUME_PROMPT_MAX_CHARS.toLocaleString()} characters of role context.</span>
              <button type="submit" className="primary-action" disabled={generating}>{generating ? <ThinkingStatus /> : "Generate resume"}</button>
            </div>
          </form>

          {(generating || draft) && <article className="resume-draft-card">
            <div className="resume-draft-topline">
              <span>{hasGeneratedDraft ? "Tailored resume" : "Current public resume"}</span>
              <span>{draft?.subtitle ?? "Tailoring to the selected role"}</span>
            </div>
            {draft && <div className="resume-export-bar" aria-label="Resume export options">
              <span>Export:</span>
              <button type="button" onClick={() => void exportResume("pdf")} disabled={generating || Boolean(exporting)}>
                <Download size={14} /> {exporting === "pdf" ? "Creating PDF…" : "PDF"}
              </button>
              <button type="button" onClick={() => void exportResume("docx")} disabled={generating || Boolean(exporting)}>
                <Download size={14} /> {exporting === "docx" ? "Creating DOCX…" : "DOCX"}
              </button>
              <button type="button" onClick={() => void exportResume("markdown")} disabled={generating || Boolean(exporting)}>
                <Download size={14} /> {exporting === "markdown" ? "Creating MD…" : "Markdown"}
              </button>
              <button type="button" onClick={() => void exportResume("plaintext")} disabled={generating || Boolean(exporting)}>
                <Download size={14} /> {exporting === "plaintext" ? "Creating TXT…" : "Plain text"}
              </button>
            </div>}
            {generating ? <div className="resume-ai-loading"><ThinkingStatus /></div> : draft && (hasGeneratedDraft
              ? <SimulatedAiPre key={`${draft.subtitle}-${draft.body.length}`} text={draft.body} />
              : <pre className="simulated-ai-pre">{draft.body}</pre>)}
            {exportError && <p className="resume-export-error" role="status">{exportError}</p>}
          </article>}
          {generationError && <p className="resume-export-error" role="status">{generationError}</p>}
        </section>

        <aside className="resume-side">
          <section className="work-scale-card">
            <div className="resume-generator-topline">
              <div>
                <span className="section-kicker">Omer’s preference</span>
                <h2>Days in office</h2>
              </div>
              <span className="resume-badge"><SlidersHorizontal size={14} /> Hybrid</span>
            </div>
            <div className="days-label-row"><span>Full week in office</span><span>Hybrid</span><span>Fully remote</span></div>
            <input
              className="work-scale"
              type="range"
              min={0}
              max={5}
              step={1}
              value={officeDays}
              onChange={(event) => setOfficeDays(Number(event.target.value))}
              aria-label="Days in office"
            />
            <div className="work-scale-days">
              {Array.from({ length: 6 }, (_, index) => (
                <button key={index} type="button" className={officeDays === index ? "selected" : ""} onClick={() => setOfficeDays(index)}>
                  Day {index}
                </button>
              ))}
            </div>
            <p className="work-scale-copy">{renderWorkPreference(recommendation)}</p>
          </section>
        </aside>
      </div>

      {odinOpen && <div className="modal-backdrop odin-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setOdinOpen(false); }}>
        <section className="gallery-modal" role="dialog" aria-modal="true" aria-labelledby="odin-gallery-title" aria-describedby="odin-gallery-note">
          <button className="modal-close" type="button" onClick={() => setOdinOpen(false)} aria-label="Close Odin’s photos" autoFocus>×</button>
          <header className="gallery-header">
            <div>
              <span className="eyebrow">Meet Odin</span>
              <h2 id="odin-gallery-title">Omer’s four-legged chief of staff</h2>
            </div>
            <span className="gallery-counter" aria-live="polite">{odinIndex + 1} / {odinGallery.length}</span>
          </header>
          <figure
            className="gallery-slide"
            onTouchStart={(event) => { odinTouchStart.current = event.changedTouches[0]?.clientX ?? null; }}
            onTouchEnd={(event) => {
              if (odinTouchStart.current === null) return;
              const distance = (event.changedTouches[0]?.clientX ?? odinTouchStart.current) - odinTouchStart.current;
              odinTouchStart.current = null;
              if (Math.abs(distance) < 45) return;
              if (distance > 0) showPreviousOdinPhoto(); else showNextOdinPhoto();
            }}
          >
            <div className="gallery-image" key={currentOdinPhoto.slug}>
              {renderOdinPicture(currentOdinPhoto.slug, currentOdinPhoto.alt)}
            </div>
            <figcaption id="odin-gallery-note">
              <strong>{currentOdinPhoto.caption}</strong>
              <span>{currentOdinPhoto.note}</span>
            </figcaption>
          </figure>
          <div className="gallery-controls" aria-label="Odin photo navigation">
            <button type="button" onClick={showPreviousOdinPhoto} aria-label="Previous photo"><ChevronLeft size={20} /> Previous</button>
            <div className="gallery-dots" aria-label="Choose a photo">
              {odinGallery.map((photo, index) => <button key={photo.slug} type="button" className={index === odinIndex ? "selected" : ""} onClick={() => setOdinIndex(index)} aria-label={`View photo ${index + 1}: ${photo.caption}`} aria-current={index === odinIndex ? "true" : undefined} />)}
            </div>
            <button type="button" onClick={showNextOdinPhoto} aria-label="Next photo">Next <ChevronRight size={20} /></button>
          </div>
        </section>
      </div>}
    </section>
  );
}

export function StatusRail() {
  return <aside className="status-rail"><div className="rail-header"><span>Live system</span><Activity size={14} /></div><div className="rail-metric"><span>AI posture</span><strong>Protected</strong><small><i className="green" /> Guardrails active</small></div><div className="rail-metric"><span>Data policy</span><strong>Minimal</strong><small><i className="green" /> 30-day raw chat TTL</small></div><div className="rail-metric"><span>Usage controls</span><strong>Active</strong><small><i className="amber" /> Cost and request limits</small></div><Link to="/architecture" className="rail-link">View system map <ArrowRight size={14} /></Link><div className="rail-stack"><span>Built on</span><div><Blocks size={15} /> Cloudflare Developer Platform</div><div><GitBranch size={15} /> GitLab CI/CD ready</div></div></aside>;
}
